/**
 * SignalDetail — renders one Golden Signals entry.
 *
 * The visual language is deliberately the same as StrategyOverview: a short tag
 * chip, a bold label, an em-dash, then the sentence. Two surfaces that both say
 * "here is a rule" should not look like two different apps.
 *
 * StrategyOverview itself could not be reused: its spec fields are
 * React.ReactNode, so it is a hardcoded component spec and cannot come out of a
 * jsonb column. What is shared is the look, not the type.
 *
 * Nothing here is interactive except the chart link. A catalogue that grew an
 * action control would stop being a catalogue.
 */
import React from 'react';
import {
  Box, Card, CardContent, CardHeader, Chip, Divider, Link as MuiLink, Stack, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { useAppTheme } from '../../hooks/useAppTheme';
import { intervalLabel } from '../../utils/intervalCatalog';
import {
  GoldenSignalRow, GsCondition, GsEvidence, chartLinkFor, hydratePayload,
  GS_DIRECTION_HELP, GS_STATUS_HELP,
} from '../../services/goldenSignals.service';

const Section: React.FC<{
  title: string; subtitle?: React.ReactNode; children: React.ReactNode; outlined?: boolean;
}> = ({ title, subtitle, children, outlined }) => (
  <Card variant={outlined ? 'outlined' : 'elevation'} sx={{ mb: 2 }}>
    <CardHeader
      sx={{ pb: 0 }}
      title={<Typography variant="subtitle1" fontWeight={700}>{title}</Typography>}
      subheader={subtitle
        ? <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        : undefined}
    />
    <CardContent sx={{ pt: 1.25 }}>{children}</CardContent>
  </Card>
);

const ConditionRow: React.FC<{
  c: GsCondition; tagColor?: string; labelColor?: string;
}> = ({ c, tagColor, labelColor }) => {
  const { C } = useAppTheme();
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1.1, alignItems: 'flex-start' }}>
      {c.tag ? (
        <Chip
          label={c.tag}
          size="small"
          sx={{
            fontWeight: 700, fontSize: '0.65rem', height: 20, minWidth: 62, flexShrink: 0,
            bgcolor: tagColor || 'action.selected',
            color: tagColor ? C.onSolid : 'text.primary',
          }}
        />
      ) : null}
      <Box>
        <Typography variant="body2" component="span" sx={{ fontWeight: 700, color: labelColor }}>
          {c.label}
        </Typography>
        {c.desc ? (
          <>
            {' — '}
            <Typography variant="body2" component="span" color="text.secondary">{c.desc}</Typography>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

const EvidenceRow: React.FC<{ e: GsEvidence }> = ({ e }) => {
  const { C } = useAppTheme();
  return (
    <Box sx={{ mb: 1.4 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 800, color: C.bull }}>{e.value}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.metric}</Typography>
        {e.baseline ? (
          <Typography variant="caption" color="text.secondary">
            against a baseline of {e.baseline}
          </Typography>
        ) : null}
        {e.win ? <Chip size="small" variant="outlined" label={`win ${e.win}`} sx={{ height: 20, fontSize: '0.65rem' }} /> : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {e.dataset}
      </Typography>
      {e.note ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>{e.note}</Typography>
      ) : null}
    </Box>
  );
};

/** `mono` is for VALUES — a parameter, a level, a flag. Prose such as the market
 *  list is left in the body font: monospacing a sentence makes it read as data
 *  the reader is meant to type somewhere. 260px is the width of the longest
 *  shipped parameter label ("Candles the oscillators compute on"); anything
 *  narrower pushed that one row's value out of the column. */
const KeyVal: React.FC<{
  label: string; value: React.ReactNode; hint?: string; mono?: boolean;
}> = ({ label, value, hint, mono = true }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 0.8, alignItems: 'baseline', flexWrap: 'wrap' }}>
    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 260 }}>{label}</Typography>
    <Typography variant="body2" sx={mono ? { fontFamily: 'monospace' } : undefined}>{value}</Typography>
    {hint ? <Typography variant="caption" color="text.secondary">{hint}</Typography> : null}
  </Box>
);

/** A list of interval minutes as chart labels ("1D", "4H"). */
const tfList = (mins: number[] | undefined) =>
  (mins || []).filter(n => Number.isFinite(n) && n > 0).map(n => intervalLabel(n)).join(', ');

const SignalDetail: React.FC<{ row: GoldenSignalRow }> = ({ row }) => {
  const { C, isDark } = useAppTheme();
  const p = hydratePayload(row.payload);
  const link = chartLinkFor(row);
  const mark = p.mark;

  const dirColor = row.direction === 'LONG' ? C.bull
    : row.direction === 'SHORT' ? C.bear
      : C.neutral;

  return (
    <Box sx={{ maxWidth: 900 }}>
      {/* ---- header ---- */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          sx={{ pb: 0.5 }}
          title={
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" fontWeight={800}>{row.name}</Typography>
              <Chip
                size="small" label={row.direction} title={GS_DIRECTION_HELP[row.direction]}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: dirColor, color: C.onSolid }}
              />
              <Chip
                size="small" variant="outlined" label={row.status} title={GS_STATUS_HELP[row.status]}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
              />
              {row.builtinKey ? (
                <Chip
                  size="small" variant="outlined" label="shipped"
                  title="Seeded with the app. You can edit it freely; Restore puts the shipped text back."
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              ) : null}
            </Stack>
          }
        />
        <CardContent sx={{ pt: 1 }}>
          {row.summary ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {row.summary}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">No summary yet.</Typography>
          )}

          {row.tags?.length ? (
            <Stack direction="row" spacing={0.7} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {row.tags.map(t => (
                <Chip key={t} size="small" label={t} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              ))}
            </Stack>
          ) : null}

          {(mark?.label || link) ? (
            <>
              <Divider sx={{ my: 1.75 }} />
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                {mark?.label ? (
                  <Stack direction="row" spacing={0.9} alignItems="center">
                    <Box
                      component="span"
                      sx={{
                        width: 15, height: 15, borderRadius: '3px', flexShrink: 0,
                        bgcolor: mark.color, border: '1px solid rgba(128,128,128,0.4)',
                      }}
                    />
                    <Typography variant="body2">
                      Drawn as <b>{mark.label}</b>
                      {mark.shape === 'arrowUp' ? ', an up arrow' : mark.shape === 'arrowDown' ? ', a down arrow' : ''}
                      {mark.position === 'belowBar' ? ' below the bar' : mark.position === 'aboveBar' ? ' above the bar' : ''}
                    </Typography>
                  </Stack>
                ) : null}
                {link ? (
                  <MuiLink
                    component={RouterLink}
                    to={link}
                    data-testid="gs-chart-link"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700, fontSize: '0.85rem' }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 15 }} />
                    Show me this on a chart
                  </MuiLink>
                ) : null}
              </Stack>
              {link ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Opens {p.chart?.defaultSymbol} at {intervalLabel(p.chart?.defaultInterval || 1440)} and switches
                  the indicators this signal needs on. Like clicking the chips yourself, it becomes your
                  remembered chart setup — nothing else is turned off.
                </Typography>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* ---- what it needs ---- */}
      {(p.indicators?.length || p.markets?.length || tfList(p.timeframes?.measuredOn) || p.timeframes?.note) ? (
        <Section title="What it needs" subtitle="The chart has to be showing these for the signal to appear.">
          {(p.indicators || []).map((ind, i) => (
            <ConditionRow key={i} c={{ tag: ind.key, label: ind.label, desc: ind.note || '' }} />
          ))}
          {p.timeframes?.measuredOn?.length ? (
            <KeyVal label="Measured on" value={tfList(p.timeframes.measuredOn)} />
          ) : null}
          {p.timeframes?.alsoWorks?.length ? (
            <KeyVal label="Also holds on" value={tfList(p.timeframes.alsoWorks)} />
          ) : null}
          {p.timeframes?.weakOn?.length ? (
            <KeyVal label="Weak or break-even on" value={tfList(p.timeframes.weakOn)} />
          ) : null}
          {p.markets?.length ? <KeyVal label="Markets" value={p.markets.join(' · ')} mono={false} /> : null}
          {p.timeframes?.note ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
              {p.timeframes.note}
            </Typography>
          ) : null}
        </Section>
      ) : null}

      {/* ---- entry ---- */}
      <Section title="Entry conditions" subtitle="Every condition that has to hold for the signal to fire.">
        {p.entry?.length
          ? p.entry.map((c, i) => <ConditionRow key={i} c={c} tagColor={C.bull} />)
          : <Typography variant="body2" color="text.disabled">Not written down yet.</Typography>}
      </Section>

      {/* ---- exit ---- */}
      <Section title="Exit conditions" subtitle="What closes it, and which one wins when several could.">
        {p.exit?.length
          ? p.exit.map((c, i) => <ConditionRow key={i} c={c} tagColor={isDark ? '#42a5f5' : '#1565c0'} />)
          : <Typography variant="body2" color="text.disabled">Not written down yet.</Typography>}
      </Section>

      {/* ---- parameters ---- */}
      {p.params?.length ? (
        <Section
          title="Parameters"
          subtitle="The value as shipped, and where in the app that number actually lives."
        >
          {p.params.map((prm, i) => (
            <KeyVal key={i} label={prm.label} value={prm.value} hint={prm.source} />
          ))}
        </Section>
      ) : null}

      {/* ---- evidence ---- */}
      {p.evidence?.length ? (
        <Section title="Evidence" subtitle="What it measured, and on what.">
          {p.evidence.map((e, i) => <EvidenceRow key={i} e={e} />)}
        </Section>
      ) : null}

      {/* ---- what did not work ---- */}
      {p.notWorking?.length ? (
        <Section
          outlined
          title="What did not work"
          subtitle="Measured dead and recorded on purpose, so it is not tried again."
        >
          {p.notWorking.map((c, i) => (
            <ConditionRow key={i} c={c} tagColor={C.bear} labelColor={C.bear} />
          ))}
        </Section>
      ) : null}

      {/* ---- related + sources ---- */}
      {(p.related?.length || p.sources?.length) ? (
        <Section title="Related" subtitle="Where this signal leads, and what defines it in the code.">
          {(p.related || []).map((r, i) => (
            <ConditionRow key={i} c={{ tag: r.kind, label: r.label, desc: '' }} />
          ))}
          {p.sources?.length ? (
            <Box sx={{ mt: p.related?.length ? 1.5 : 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Defined in
              </Typography>
              {p.sources.map((s, i) => (
                <Typography
                  key={i} variant="caption"
                  sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary', wordBreak: 'break-all' }}
                >
                  {s}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Section>
      ) : null}

      {/* ---- notes ---- */}
      {p.notes ? (
        <Section title="Notes">
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
            {p.notes}
          </Typography>
        </Section>
      ) : null}
    </Box>
  );
};

export default SignalDetail;
